import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Page } from '../../components/layout/AppShell'
import { PageHeader } from '../../components/layout/PageHeader'
import { Badge, DateInput, MoneyDelta, Panel, Spinner, ErrorState, Table, type Column } from '../../components/ui'
import { useMeritOrderDispatch, type MeritOrderHour } from './useMeritOrder'

function tomorrowISO(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

function fmt(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined) return '–'
  return n.toFixed(digits)
}

function regimeTone(r: string): 'red' | 'blue' | 'neutral' {
  if (r === 'scarcity') return 'red'
  if (r === 'oversupply') return 'blue'
  return 'neutral'
}

export function MeritOrderPage() {
  const navigate = useNavigate()
  const [date, setDate] = useState(tomorrowISO)
  const { data, isLoading, isError } = useMeritOrderDispatch(date)

  const hoursWithActuals = data?.hours.filter((h) => h.actual_fix1_pln !== null).length ?? 0
  const mae = data?.hours
    .filter((h) => h.gap_fix1_pln !== null)
    .reduce((acc, h, _, arr) => acc + Math.abs(h.gap_fix1_pln!) / arr.length, 0)

  const columns: Column<MeritOrderHour>[] = [
    { key: 'hour', header: 'Hour', render: (h) => `${String(h.hour).padStart(2, '0')}:00` },
    { key: 'model', header: 'Model (PLN)', align: 'right', render: (h) => fmt(h.clearing_price_pln) },
    { key: 'fix1', header: 'Fix 1 (PLN)', align: 'right', render: (h) => fmt(h.actual_fix1_pln) },
    { key: 'fix2', header: 'Fix 2 (PLN)', align: 'right', render: (h) => fmt(h.actual_fix2_pln) },
    { key: 'gap1', header: 'Gap vs Fix 1', align: 'right', render: (h) => <MoneyDelta value={h.gap_fix1_pln} /> },
    { key: 'gap2', header: 'Gap vs Fix 2', align: 'right', render: (h) => <MoneyDelta value={h.gap_fix2_pln} /> },
    { key: 'fuel', header: 'Marginal fuel', render: (h) => h.marginal_fuel_class ?? '–' },
    { key: 'unit', header: 'Marginal unit', render: (h) => h.marginal_unit_id ?? '–' },
    { key: 'regime', header: 'Regime', render: (h) => <Badge tone={regimeTone(h.regime)}>{h.regime}</Badge> },
  ]

  return (
    <Page
      header={
        <PageHeader
          crumbs={[{ label: 'Models', onClick: () => navigate({ to: '/models' }) }, { label: 'Merit order' }]}
          actions={<DateInput value={date} onChange={setDate} />}
        />
      }
    >
      {isLoading ? (
        <Spinner label="Loading dispatch…" />
      ) : isError ? (
        <ErrorState message="No dispatch available for this date." />
      ) : data ? (
        <>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm text-gray-600">
            <span>
              <span className="text-gray-400">Stack:</span> {data.stack.length} units,{' '}
              {data.hours[0]?.total_available_mw.toFixed(0)} MW
            </span>
            <span>
              <span className="text-gray-400">TGE coverage:</span> {hoursWithActuals}/24 hours
            </span>
            {hoursWithActuals > 0 && mae !== undefined && (
              <span>
                <span className="text-gray-400">MAE vs Fix 1:</span>{' '}
                <span className="font-mono text-gray-800">{mae.toFixed(1)} PLN/MWh</span>
              </span>
            )}
            <span className="text-gray-400">
              TTF {data.prices.ttf_eur.toFixed(1)} EUR · ARA {data.prices.ara_usd.toFixed(1)} USD · EUA{' '}
              {data.prices.eua_eur.toFixed(1)} EUR · EURPLN {data.prices.eurpln.toFixed(3)}
            </span>
          </div>
          <Panel>
            <Table columns={columns} rows={data.hours} empty="No hours." />
          </Panel>
        </>
      ) : null}
    </Page>
  )
}
